<template>
  <div class="footer">
    <div class="summary">
      {{ `${pagination.pageStart} - ${pagination.pageStop} av ${pagination.totalItems}` }}
    </div>

    <div class="controls">
      <label class="items-per-page">
        <span>{{ pagination.pageSizeText }}</span>
        <AppSelect
          class="items-per-page-select"
          :value="pagination.pageSize.toString()"
          :options="pageSizeSelectOptions"
          mandatory
          slim
          borderless
          @input="handlePageSizeChange"
        />
      </label>

      <div class="pagination">
        <button
          type="button"
          class="pagination-button"
          :disabled="pagination.isPreviousPageDisabled"
          aria-label="Föregående sida"
          @click="pagination.previousPage"
        >
          <SvgIcon :path="mdiChevronLeft" size="18" />
        </button>
        <span class="pagination-page">{{ pagination.currentPage }} / {{ pagination.totalPages }}</span>
        <button
          type="button"
          class="pagination-button"
          :disabled="pagination.isNextPageDisabled"
          aria-label="Nästa sida"
          @click="pagination.nextPage"
        >
          <SvgIcon :path="mdiChevronRight" size="18" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js'

import type { AppTablePaginationModel } from './app-table-list.types'
import AppSelect from '../AppSelect/AppSelect.vue'
import SvgIcon from '../SvgIcon/SvgIcon.vue'

const props = defineProps<{
  pagination: AppTablePaginationModel
}>()

const pageSizeSelectOptions = computed(() =>
  props.pagination.pageSizeOptions.map((value) => ({
    text: value === -1 ? (props.pagination.pageSizeAllText ?? 'Alla') : value.toString(),
    value: value.toString(),
  })),
)

const handlePageSizeChange = (value: string) => {
  if (!value) return
  void props.pagination.setPageSize(Number(value))
}
</script>

<style lang="scss" scoped>
.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding: 14px 16px 4px;
}

.summary {
  color: var(--color-fg-default);
}

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-left: auto;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
}

.items-per-page-select {
  min-width: 84px;
}

.items-per-page > span {
  white-space: nowrap;
}

.pagination {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--color-border-default);
  border-radius: 4px;
  background: var(--color-canvas-default);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: 2px solid var(--color-brand-default);
    outline-offset: 1px;
  }
}

.pagination-page {
  min-width: 72px;
  text-align: center;
}

@media (max-width: 640px) {
  .footer,
  .controls {
    align-items: stretch;
  }

  .controls {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
